import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Use relative URL in production (Docker) or absolute URL in development
const API_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8002/api');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/me`);
      setUser(response.data.user);
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/login`, { email, password });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      console.error('API URL:', API_URL);
      
      let errorMessage = 'Login failed';
      
      if (error.response) {
        // Server responded with error
        errorMessage = error.response.data?.error || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // Request made but no response
        errorMessage = 'Cannot connect to server. Please check if the backend is running.';
      } else {
        // Something else happened
        errorMessage = error.message || 'Login failed';
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const register = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/register`, { email, password });
      const user = response.data.user;
      
      // Use the normalized email from the response for login
      const normalizedEmail = user.email || email.toLowerCase().trim();
      
      // Automatically log in the user after successful registration
      try {
        const loginResponse = await axios.post(`${API_URL}/login`, { 
          email: normalizedEmail, 
          password: password 
        });
        const { token } = loginResponse.data;
        localStorage.setItem('token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(user);
        return { success: true, user };
      } catch (loginError) {
        // If auto-login fails, log the error with details
        console.error('Auto-login after registration failed:', loginError);
        console.error('Login error response:', loginError.response?.data);
        console.error('Attempted email:', normalizedEmail);
        
        // Return success but with a warning
        return {
          success: false,
          error: loginError.response?.data?.error || 'Account created but login failed. Please try logging in manually with your email and password.'
        };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'Registration failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const trackVisit = async (appUrl) => {
    if (!user) return;
    try {
      await axios.post(`${API_URL}/track-visit`, { app_url: appUrl });
    } catch (error) {
      console.error('Failed to track visit:', error);
    }
  };

  const getToken = () => {
    return localStorage.getItem('token');
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    trackVisit,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    token: getToken()
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

